/**
 * Browser-side telemetry for conversation isolation.
 *
 * It reports two different mistakes, and keeps them apart:
 *
 *   - `foreign_agent`: a restricted agent has a conversation on screen that is
 *     assigned to somebody else. This is the isolation bug the backend enforces
 *     against, and the reason the module exists.
 *   - `space_mismatch`: ANY user — superadmin included — has a conversation on
 *     screen whose channel is not one of the channels of the space they are
 *     currently looking at. That is not a permissions leak (the channel does
 *     belong to one of their spaces), it is a VIEW bug: the list query filters
 *     by `channelId_in` correctly, so a conversation that only appears through
 *     the live insert path and vanishes on reload came in through a door that
 *     never checked the active space.
 *
 * The second signal is the one the role gate used to be blind to: a superadmin
 * is exempt from `foreign_agent` by definition, which is exactly the account
 * that reported seeing a "Ventas" conversation while sitting in "Soporte".
 *
 * The browser is also the only place that can observe one of the four doors: a
 * conversation left over in the store from an earlier session is not being
 * delivered by anything, so no amount of server-side logging can find it.
 *
 * This module REPORTS. It never filters, never hides, never redirects and never
 * decides anything — the backend is the enforcer. Two properties matter as much
 * as the reporting:
 *   - zero latency on the render path. Every check is a comparison over data
 *     already in memory, and the dispatch is fire-and-forget.
 *   - it can never break the dashboard. Every entry point swallows its own
 *     errors: telemetry that can throw is worse than no telemetry.
 */

/**
 * Roles exempt from the assignee check.
 *
 * This duplicates the backend's `agentScope.service`. That duplication is
 * deliberate and worth naming: the backend is the ENFORCER and this is only a
 * REPORTER, so drift here produces noisy or missing telemetry, never a security
 * hole. It lives in one named export so the divergence is greppable from both
 * repositories.
 *
 * Note that it gates ONLY `foreign_agent`. `space_mismatch` applies to every
 * role, because the bug it looks for is a rendering mistake rather than a
 * permission one.
 */
export const UNRESTRICTED_ROLE_NAMES = [
  "superadmin",
  "admin",
  "groupadmin",
  "AI_BOT",
];

/**
 * Where a write to `state.conversations.data` came from, as a closed set.
 *
 * The label is the whole diagnostic value of a report, so it is stated by the
 * DISPATCH SITE (`withTelemetrySource`) and never guessed from the action type:
 * `SET_CONVERSATIONS` is dispatched from ~20 places, and only a few of them are
 * a Pusher event inserting a conversation. An unlabelled write is `redux` — "a
 * local rewrite of the list, door not stated" — which is honest; `pusher` on a
 * pagination result was not.
 */
export const TELEMETRY_SOURCES = {
  QUERY: "query",
  PAGINATION: "pagination",
  PUSHER: "pusher",
  SELECTION: "selection",
  // The open conversation, inspected on its own because a deep link puts it
  // in `state.conversations.current` without ever adding it to the list.
  SELECTED: "selected",
  // The unassigned-agent GraphQL subscription rewriting the list. Never the
  // list baseline: only a `query` load says which space the list was loaded for.
  SUBSCRIPTION: "subscription",
  // The Board view merging its own fetch into the list (addConversationsAction).
  BOARD: "board",
  UPDATE: "update",
  REDUX: "redux",
};

const TELEMETRY_SOURCE_VALUES = Object.keys(TELEMETRY_SOURCES).map(
  (key) => TELEMETRY_SOURCES[key]
);

/** The label if it belongs to the closed set, otherwise null. */
export function knownTelemetrySource(raw) {
  return typeof raw === "string" && TELEMETRY_SOURCE_VALUES.indexOf(raw) !== -1
    ? raw
    : null;
}

/**
 * Labels an action at its dispatch site. Reducers read `type` and `payload`
 * only, so `meta` changes nothing for them or for any existing dispatcher.
 */
export function withTelemetrySource(action, source) {
  if (!action || typeof action !== "object") return action;
  return { ...action, meta: { ...action.meta, telemetrySource: source } };
}

/**
 * The two signals, as a closed set.
 *
 * They travel in the same batch and are told apart by this field, so the
 * backend can count, alert on and graph them separately without parsing
 * anything. Never widen this without widening the backend's enum first.
 */
export const ANOMALY_KINDS = {
  FOREIGN_AGENT: "foreign_agent",
  SPACE_MISMATCH: "space_mismatch",
};

/** Beyond this many remembered findings the oldest are forgotten. A tab can
 *  stay open all day, and an unbounded dedupe set is a slow leak. */
const DEFAULT_MAX_TRACKED = 500;

const DEFAULT_MAX_BATCH = 20;

/**
 * Longest a finding waits in the queue before it is sent.
 *
 * Without it a batch left only at 20 events or when the tab went hidden, and an
 * agent who keeps one tab in front all day (the usual case) could hold a single
 * finding for hours. One timer at most, armed by the first pending finding and
 * never pushed back by later ones, so this is also the most often the timer can
 * send: one request per minute per tab, only while there is something to say.
 */
const DEFAULT_FLUSH_MS = 60 * 1000;

/**
 * Per-field character budget for the serialized batch.
 *
 * `sendTelemetry` posts the batch with `keepalive: true`, and a keepalive body
 * is capped at 64 KiB by the browser: go over it and `fetch` rejects, so the
 * batch is lost. Capping the batch at 20 events is not enough on its own,
 * because every field here comes from a server payload this module does not
 * control — `prov` in particular used to be copied by reference, so its size
 * (and even its serializability: a cyclic object makes `JSON.stringify` throw)
 * was whatever the payload happened to carry.
 *
 * With a fixed schema and these caps the worst case is arithmetic instead of a
 * hope: 8 scalar fields (~600 chars) + a 7-field seal (~790 chars) + JSON key
 * overhead ≈ 1.5 KB per event, × 20 events ≈ 30 KB — comfortably under the
 * 64 KiB limit with room for multi-byte characters.
 */
const MAX_ID_CHARS = 64;
const MAX_LABEL_CHARS = 64;
const MAX_ROUTE_CHARS = 256;
const MAX_SEAL_FIELD_CHARS = 128;

/**
 * The seal's fixed schema: version, door, user, conversation, agent, issue time
 * and signature — exactly the backend's `ProvenanceSeal`
 * (`conversationProvenance.service.ts`). `t` is part of the SIGNED payload
 * (`[v, d, u, c, a, t].join('|')`): without it `verifySeal` answers `missing`
 * and every sealed report is classified `unsealed`.
 * Anything the server adds beyond these is dropped rather than forwarded, so a
 * change on the other side can never inflate this payload.
 */
const SEAL_FIELDS = ["v", "d", "u", "c", "a", "t", "s"];

/** A trimmed non-empty string, or null. Never coerces a non-string. */
function usableString(raw) {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** `usableString` with a hard length budget. */
function cappedString(raw, maxChars) {
  const value = usableString(raw);
  if (value === null) return null;
  return value.length > maxChars ? value.slice(0, maxChars) : value;
}

/** Matches the backend's rule: exact, case sensitive, malformed means restricted. */
export function isRestrictedAgentUser(user) {
  try {
    const name = user && user.rol ? user.rol.name : undefined;
    if (typeof name !== "string") return true;
    return UNRESTRICTED_ROLE_NAMES.indexOf(name) === -1;
  } catch (e) {
    return true;
  }
}

/**
 * Whether a foreign assignee is worth reporting for this user.
 *
 * Exactly the backend's rule now that it enforces isolation for every company:
 * every role except the four in `UNRESTRICTED_ROLE_NAMES` only ever receives
 * its own conversations, so a foreign one on screen is a finding whatever the
 * role is called. Community Manager used to be exempted here because, outside
 * the backend's company allowlist, the list query legitimately served it the
 * whole inbox; that allowlist is gone. Malformed input reports, which is the
 * noisy-but-safe direction.
 */
export function reportsForeignAgent(user) {
  return isRestrictedAgentUser(user);
}

/**
 * The assignee, from either shape the payloads use, with the backend's
 * precedence (`agentScope.service.assignedAgentId`): the flat `agentId` first,
 * then `agent.id`. Reading them in a different order than the enforcer would
 * report a conversation the backend considers the user's own, or miss one it
 * considers foreign.
 */
function assigneeOf(conversation) {
  if (!conversation || typeof conversation !== "object") return null;
  const flat = usableString(conversation.agentId);
  if (flat) return flat;
  return conversation.agent && typeof conversation.agent === "object"
    ? usableString(conversation.agent.id)
    : null;
}

/**
 * The conversation's channel, from either shape the payloads use.
 *
 * The list query and the deep-link path deliver `channel: { id }`; some paths
 * (Boards, and anything that re-maps the node) carry a flat `channelId`. A
 * conversation carries no space of its own — it belongs to a space only
 * through its channel, which is exactly the relation the server filters on
 * (`channelId_in`).
 */
function channelIdOf(conversation) {
  if (!conversation || typeof conversation !== "object") return null;
  const nested =
    conversation.channel && typeof conversation.channel === "object"
      ? usableString(conversation.channel.id)
      : null;
  return nested || usableString(conversation.channelId);
}

/**
 * One seal field, kept as-is when it is a finite number or a boolean and
 * truncated when it is a string. Anything else becomes null rather than being
 * forwarded: an object here is how an unbounded payload got in.
 */
function sealField(raw) {
  if (typeof raw === "number") return isFinite(raw) ? raw : null;
  if (typeof raw === "boolean") return raw;
  if (typeof raw !== "string") return null;
  return raw.length > MAX_SEAL_FIELD_CHARS
    ? raw.slice(0, MAX_SEAL_FIELD_CHARS)
    : raw;
}

/**
 * The provenance seal, copied into a fixed schema instead of by reference:
 * exactly the seven fields, or null. Also what the mappers use to carry the
 * seal into the store, so a server payload never lands there by reference.
 */
export function normalizeSeal(prov) {
  if (!prov || typeof prov !== "object" || Array.isArray(prov)) return null;
  const seal = {};
  for (let i = 0; i < SEAL_FIELDS.length; i += 1) {
    const key = SEAL_FIELDS[i];
    seal[key] = sealField(prov[key]);
  }
  return seal;
}

/** The path the user is on, capped. Absent outside a browser. */
function currentRoute() {
  if (typeof window === "undefined" || !window.location) return null;
  return cappedString(window.location.pathname, MAX_ROUTE_CHARS);
}

/**
 * The channels of the space the user is looking at, as a lookup index.
 *
 * Returns null — meaning "do not judge" — whenever the active space cannot be
 * read: `state.spaces.selected` is legitimately `{}` until the bootstrap query
 * resolves, and reporting every conversation on screen during those first
 * milliseconds would bury the real signal under a full page of noise.
 */
function activeChannelIndex(activeSpace) {
  if (!activeSpace || typeof activeSpace !== "object") return null;
  const channels = activeSpace.channels;
  if (!Array.isArray(channels) || channels.length === 0) return null;

  const index = {};
  let known = 0;
  for (let i = 0; i < channels.length; i += 1) {
    const channel = channels[i];
    const id =
      channel && typeof channel === "object"
        ? usableString(channel.id)
        : usableString(channel);
    if (!id) continue;
    index[id] = true;
    known += 1;
  }
  return known > 0 ? index : null;
}

/** Every event carries the same fixed schema, whatever its kind. */
function buildEvent(kind, conversation, conversationId, src, spaceId) {
  return {
    kind,
    conversationId,
    assignedTo: cappedString(assigneeOf(conversation), MAX_ID_CHARS),
    channelId: cappedString(channelIdOf(conversation), MAX_ID_CHARS),
    spaceId: spaceId || null,
    src: cappedString(src, MAX_LABEL_CHARS) || "unknown",
    prov: normalizeSeal(conversation.prov),
    route: currentRoute(),
  };
}

/**
 * The findings for one conversation: none, one, or both kinds.
 *
 * `reportsForeign` and `ownId` gate only the assignee check; the space check
 * runs for every role, which is the whole point of adding it.
 */
function inspectConversation(context, conversation) {
  const conversationId =
    conversation && typeof conversation === "object"
      ? cappedString(conversation.id, MAX_ID_CHARS)
      : null;
  if (!conversationId) return [];

  const found = [];
  const assignedTo = assigneeOf(conversation);
  if (context.reportsForeign && context.ownId && assignedTo && assignedTo !== context.ownId) {
    found.push(
      buildEvent(
        ANOMALY_KINDS.FOREIGN_AGENT,
        conversation,
        conversationId,
        context.src,
        context.spaceId
      )
    );
  }

  const channelId = channelIdOf(conversation);
  const outsideSpace =
    context.judgeSpace &&
    context.activeChannels !== null &&
    channelId !== null &&
    !Object.prototype.hasOwnProperty.call(context.activeChannels, channelId);
  if (outsideSpace) {
    found.push(
      buildEvent(
        ANOMALY_KINDS.SPACE_MISMATCH,
        conversation,
        conversationId,
        context.src,
        context.spaceId
      )
    );
  }
  return found;
}

/**
 * The conversations on screen that should not be there.
 *
 * Unassigned conversations are deliberately NOT reported as `foreign_agent`.
 * The bug being diagnosed is about conversations that already have an agent and
 * another one saw them; reporting unassigned ones during the rollout would bury
 * that signal under its own noise. They are still eligible for
 * `space_mismatch`, which does not depend on the assignee at all.
 *
 * `activeSpace` is `state.spaces.selected`. Omitting it disables the space
 * check and leaves the previous behaviour untouched. `options.judgeSpace ===
 * false` disables it too: the reporter passes that while the list on screen was
 * not loaded for the active space (see `createTelemetryReporter`).
 */
export function findAnomalies(user, conversations, src, activeSpace, options) {
  try {
    if (!Array.isArray(conversations)) return [];

    const context = {
      reportsForeign: reportsForeignAgent(user),
      judgeSpace: !(options && options.judgeSpace === false),
      ownId: cappedString(user && user.id, MAX_ID_CHARS),
      src,
      activeChannels: activeChannelIndex(activeSpace),
      spaceId:
        activeSpace && typeof activeSpace === "object"
          ? cappedString(activeSpace.id, MAX_ID_CHARS)
          : null,
    };

    let out = [];
    for (let i = 0; i < conversations.length; i += 1) {
      const found = inspectConversation(context, conversations[i]);
      if (found.length > 0) out = out.concat(found);
    }
    return out;
  } catch (e) {
    return [];
  }
}

/**
 * The bounded memory of what was already reported.
 *
 * Deduped per conversation AND per kind: the two signals are independent
 * findings about the same conversation, and suppressing one because the other
 * was already reported would hide half the diagnosis.
 */
function createFindingIndex(maxTracked) {
  let tracked = [];
  let index = {};
  const keyOf = (anomaly) => `${anomaly.kind}:${anomaly.conversationId}`;

  return {
    has: (anomaly) => Object.prototype.hasOwnProperty.call(index, keyOf(anomaly)),
    remember(anomaly) {
      const key = keyOf(anomaly);
      index[key] = true;
      tracked.push(key);
      while (tracked.length > maxTracked) delete index[tracked.shift()];
    },
    /** Forgets every finding of one kind and leaves the other kind alone. */
    forgetKind(kind) {
      const prefix = `${kind}:`;
      const kept = [];
      for (let i = 0; i < tracked.length; i += 1) {
        if (tracked[i].indexOf(prefix) === 0) delete index[tracked[i]];
        else kept.push(tracked[i]);
      }
      tracked = kept;
    },
    clear() {
      tracked = [];
      index = {};
    },
    size: () => tracked.length,
  };
}

/**
 * Accumulates findings and hands them to `send` in bounded batches.
 *
 * `inspected` is counted separately from anomalies, and reported even when
 * nothing is wrong: it is the only thing that lets the daily heartbeat tell
 * "no bug" apart from "the detector is not running", which is exactly the
 * distinction that could not be made in June 2026.
 */
export function createTelemetryReporter(options) {
  const config = options || {};
  const send = typeof config.send === "function" ? config.send : null;
  const maxBatch =
    typeof config.maxBatch === "number" && config.maxBatch > 0
      ? config.maxBatch
      : DEFAULT_MAX_BATCH;
  const maxTracked =
    typeof config.maxTracked === "number" && config.maxTracked > 0
      ? config.maxTracked
      : DEFAULT_MAX_TRACKED;
  const flushMs =
    typeof config.flushMs === "number" &&
    isFinite(config.flushMs) &&
    config.flushMs > 0
      ? config.flushMs
      : DEFAULT_FLUSH_MS;
  let flushTimer = null;

  let pending = [];
  let inspected = 0;
  const findings = createFindingIndex(maxTracked);
  let identity = null;
  // The space the list on screen was LOADED for. `undefined` means no list has
  // been loaded yet, which is different from a space with no readable id.
  let listSpaceId;
  let lastSpaceId;

  /**
   * Whether the list in the store can be judged against the active space.
   *
   * `changeSpace` dispatches SET_SELECTED, awaits, and only then
   * GET_CONVERSATIONS — which sets `fetching` WITHOUT clearing `data`. Until
   * GET_CONVERSATIONS_SUCCESS lands, the store holds the PREVIOUS space's inbox
   * against the new space, and any rewrite of it (a delivery receipt is enough)
   * would flag every conversation in it. So the space is judged only when the
   * list is not being fetched AND the last loaded list was loaded for this
   * space; `fetching` alone does not cover the gap before it is set.
   */
  function observeList(src, activeSpace, listState) {
    const spaceId =
      activeSpace && typeof activeSpace === "object"
        ? cappedString(activeSpace.id, MAX_ID_CHARS)
        : null;
    const fetching = !!(
      listState &&
      typeof listState === "object" &&
      listState.fetching === true
    );
    if (spaceId !== lastSpaceId) {
      // A space finding is only meaningful for the space it was made in:
      // remembering it across a switch is how a false flag on a leftover inbox
      // used to silence the real one on the same conversation. Inside one space
      // the dedupe stays per conversation — every scrolled page is a list
      // reload, and it must not re-report the same finding.
      findings.forgetKind(ANOMALY_KINDS.SPACE_MISMATCH);
      lastSpaceId = spaceId;
    }
    if (src === TELEMETRY_SOURCES.QUERY && !fetching) listSpaceId = spaceId;
    return !fetching && listSpaceId !== undefined && listSpaceId === spaceId;
  }

  /**
   * Forget everything when the signed-in user changes.
   *
   * The reporter is a module-level singleton and logout is client-side routing
   * (`history.replace('/login')`), so it outlives the session: without this,
   * agent A's pending events would go out under agent B's bearer token, and
   * the dedupe index would suppress B's report for a conversation A had
   * already reported. Only transitions between two KNOWN identities reset, so
   * a momentary state with no user (mid-login, a cleared store) does not throw
   * away a batch that is still B's.
   */
  function observeIdentity(user) {
    try {
      const next = cappedString(user && user.id, MAX_ID_CHARS);
      if (next === null) return;
      if (identity === next) return;
      if (identity !== null) {
        disarmFlushTimer();
        pending = [];
        inspected = 0;
        findings.clear();
        listSpaceId = undefined;
        lastSpaceId = undefined;
      }
      identity = next;
    } catch (e) {
      // Never break a dispatch over telemetry bookkeeping.
    }
  }

  function disarmFlushTimer() {
    if (flushTimer === null) return;
    try {
      clearTimeout(flushTimer);
    } catch (e) {
      // A missing timer API must not break a dispatch.
    }
    flushTimer = null;
  }

  /** Arms the single timed flush, unless one is already pending. */
  function armFlushTimer() {
    if (flushTimer !== null || typeof setTimeout !== "function") return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flush();
    }, flushMs);
  }

  function flush() {
    disarmFlushTimer();
    try {
      if (!send) return;
      if (pending.length === 0 && inspected === 0) return;
      const events = pending;
      const count = inspected;
      pending = [];
      inspected = 0;
      send({ events, inspected: count });
    } catch (e) {
      // Telemetry must never surface to the user.
      pending = [];
      inspected = 0;
    }
  }

  function record(user, conversations, src, activeSpace, listState) {
    try {
      observeIdentity(user);
      if (Array.isArray(conversations)) inspected += conversations.length;

      const judgeSpace = observeList(src, activeSpace, listState);
      const anomalies = findAnomalies(user, conversations, src, activeSpace, {
        judgeSpace,
      });
      for (let i = 0; i < anomalies.length; i += 1) {
        const anomaly = anomalies[i];
        if (findings.has(anomaly)) continue;
        findings.remember(anomaly);
        pending.push(anomaly);
        if (pending.length >= maxBatch) flush();
        else armFlushTimer();
      }
    } catch (e) {
      // Never break a render over telemetry.
    }
  }

  return {
    record,
    flush,
    observeIdentity,
    trackedCount: () => findings.size(),
  };
}

export default createTelemetryReporter;
